/**
 * Product-neutral PostHog experiment contracts.
 *
 * Product registries own the concrete contracts. This module only owns the shape,
 * validation, and safe evaluation boundary shared by those registries.
 */

export type TPostHogExperimentVariants = readonly ["control", ...string[]]

export interface IPostHogExperimentContract<
  TKey extends string = string,
  TVariants extends TPostHogExperimentVariants = TPostHogExperimentVariants,
  TGoalEvent extends string = string,
> {
  readonly key: TKey
  readonly displayName: string
  readonly variants: TVariants
  readonly fallbackVariant: TVariants[number]
  readonly goalEvent: TGoalEvent
  readonly measurementEnvironment: "prod"
  readonly hypothesis: string
  readonly owner: string
}

export type TPostHogExperimentContractInvalidReason =
  | "not_an_object"
  | "invalid_key"
  | "invalid_display_name"
  | "invalid_variants"
  | "control_must_be_first"
  | "duplicate_variant"
  | "invalid_fallback_variant"
  | "fallback_variant_not_in_variants"
  | "invalid_goal_event"
  | "invalid_measurement_environment"
  | "invalid_hypothesis"
  | "invalid_owner"

export type TPostHogExperimentContractValidationResult =
  | {
      readonly valid: true
    }
  | {
      readonly valid: false
      readonly reason: TPostHogExperimentContractInvalidReason
    }

export type TPostHogExperimentEvaluationReason = "missing_variant" | "unknown_variant" | "invalid_contract"

export type TPostHogExperimentEvaluationResult<TVariant extends string> =
  | {
      readonly status: "resolved"
      readonly variant: TVariant
      readonly rawValue: unknown
    }
  | {
      readonly status: "fallback"
      readonly variant: TVariant
      readonly rawValue: unknown
      readonly reason: TPostHogExperimentEvaluationReason
      readonly contractReason?: TPostHogExperimentContractInvalidReason
    }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0

/**
 * Validate an untrusted contract-shaped value before a registry or tooling consumer uses it.
 * The result is deliberately small and serializable so audit tooling can report the reason.
 */
export const validatePostHogExperimentContract = (value: unknown): TPostHogExperimentContractValidationResult => {
  if (!isRecord(value)) return { valid: false, reason: "not_an_object" }
  if (!isNonEmptyString(value.key)) return { valid: false, reason: "invalid_key" }
  if (!isNonEmptyString(value.displayName)) return { valid: false, reason: "invalid_display_name" }

  const variants = value.variants
  if (
    !Array.isArray(variants) ||
    variants.length === 0 ||
    variants.some((variant: unknown) => !isNonEmptyString(variant))
  ) {
    return { valid: false, reason: "invalid_variants" }
  }
  if (variants[0] !== "control") return { valid: false, reason: "control_must_be_first" }
  if (new Set(variants).size !== variants.length) return { valid: false, reason: "duplicate_variant" }

  if (!isNonEmptyString(value.fallbackVariant)) return { valid: false, reason: "invalid_fallback_variant" }
  if (!variants.some((variant: unknown) => variant === value.fallbackVariant)) {
    return { valid: false, reason: "fallback_variant_not_in_variants" }
  }
  if (!isNonEmptyString(value.goalEvent)) return { valid: false, reason: "invalid_goal_event" }
  if (value.measurementEnvironment !== "prod") {
    return { valid: false, reason: "invalid_measurement_environment" }
  }
  if (!isNonEmptyString(value.hypothesis)) return { valid: false, reason: "invalid_hypothesis" }
  if (!isNonEmptyString(value.owner)) return { valid: false, reason: "invalid_owner" }

  return { valid: true }
}

/**
 * Define a contract while preserving literal key, goal-event, and variant unions.
 * Runtime validation protects registries assembled from configuration or generated data.
 */
export const definePostHogExperiment = <
  const TKey extends string,
  const TVariants extends TPostHogExperimentVariants,
  const TFallbackVariant extends TVariants[number],
  const TGoalEvent extends string,
>(
  contract: Omit<IPostHogExperimentContract<TKey, TVariants, TGoalEvent>, "fallbackVariant"> & {
    readonly fallbackVariant: TFallbackVariant
  },
): IPostHogExperimentContract<TKey, TVariants, TGoalEvent> => {
  const validation = validatePostHogExperimentContract(contract)
  if (validation.valid === false) {
    throw new Error(`Invalid PostHog experiment contract: ${validation.reason}`)
  }
  return contract
}

/**
 * Resolve an untrusted PostHog result against a validated contract.
 * Unknown, nullish, and invalid-contract values never escape as typed variants.
 */
export const evaluatePostHogExperimentVariant = <
  TKey extends string,
  TVariants extends TPostHogExperimentVariants,
  TGoalEvent extends string,
>(
  contract: IPostHogExperimentContract<TKey, TVariants, TGoalEvent>,
  rawValue: unknown,
): TPostHogExperimentEvaluationResult<TVariants[number]> => {
  const validation = validatePostHogExperimentContract(contract)
  if (validation.valid === false) {
    return {
      status: "fallback",
      variant: "control",
      rawValue,
      reason: "invalid_contract",
      contractReason: validation.reason,
    }
  }

  if (rawValue === null || rawValue === undefined) {
    return { status: "fallback", variant: contract.fallbackVariant, rawValue, reason: "missing_variant" }
  }

  const resolvedVariant = contract.variants.find((variant) => variant === rawValue)
  if (resolvedVariant === undefined) {
    return { status: "fallback", variant: contract.fallbackVariant, rawValue, reason: "unknown_variant" }
  }

  return { status: "resolved", variant: resolvedVariant, rawValue }
}
